import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useParams} from '@github-ui/react-router'
import {ThreePanesLayout} from '@github-ui/three-panes-layout'
import {useSearchParams} from '@github-ui/use-navigate'
import {Spinner} from '@primer/react'

import {ProjectList} from '../components/projects/ProjectList'
import {ProjectsActions} from '../components/projects/ProjectsActions'
import styles from '../components/projects/RepositoryProjects.module.css'
import {useProjects} from '../components/projects/use-projects'
import {getRepoIssuesSidebarPanes} from '../components/sidebar/get-repo-issues-sidebar-panes'
import {AnalyticsWrapper} from './AnalyticsWrapper'

export function RepositoryProjectsPage() {
  const {owner = '', repo = ''} = useParams<{owner: string; repo: string}>()
  const [searchParams] = useSearchParams()
  const state = searchParams.get('state') === 'closed' ? 'closed' : 'open'

  const {data, isPending, isError, isFetchingNextPage, hasNextPage, fetchNextPage} = useProjects({
    owner,
    repo,
    state,
  })

  if (!isFeatureEnabled('issue_pinned_views')) {
    return null
  }

  const firstPage = data?.pages[0]
  const projects = data?.pages.flatMap(page => page.projects) ?? []
  const sidebarPanes = getRepoIssuesSidebarPanes()

  return (
    <AnalyticsWrapper category="Repository Projects Index">
      <ThreePanesLayout
        contentAs="div"
        resizeable={false}
        leftPaneWidth="medium"
        leftPane={sidebarPanes.leftPane}
        collapsibleLeftPane={sidebarPanes.collapsibleLeftPane}
        middlePane={
          <div className={styles.middlePaneWrapper}>
            <ErrorBoundary
              boundaryName="RepositoryProjectsPage"
              fallback={<p>Something went wrong loading projects.</p>}
            >
              <ProjectsActions />
              {isError ? (
                <p role="alert">Something went wrong loading projects. Please try again.</p>
              ) : isPending ? (
                <div className={styles.loadingContainer} role="status">
                  <Spinner size="large" />
                </div>
              ) : firstPage ? (
                <ProjectList
                  projects={projects}
                  openCount={firstPage.openCount}
                  closedCount={firstPage.closedCount}
                  hasNextPage={hasNextPage}
                  loadingMore={isFetchingNextPage}
                  onLoadMore={() => {
                    void fetchNextPage()
                  }}
                  viewerCanPush={firstPage.viewerCanPush}
                  ownerLogin={firstPage.ownerLogin}
                  ownerIsOrganization={firstPage.ownerIsOrganization}
                />
              ) : null}
            </ErrorBoundary>
          </div>
        }
      />
    </AnalyticsWrapper>
  )
}
